import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, updateDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

export default function Notifications({ user, isManager, onClose }) {
  const { lang, isRTL } = useLang();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const nSnap = await getDocs(query(
        collection(db, 'notifications'),
        where('destinataireId', '==', user.uid)
      ));
      const sorted = nSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
          const db2 = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
          return db2 - da;
        });
      setNotifications(sorted);
    } catch (e) { console.log('Erreur notifs:', e); }
    setLoading(false);
  };

  const marquerLu = async (id) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { lu: true });
      setNotifications(ns => ns.map(n => n.id === id ? { ...n, lu: true } : n));
    } catch (e) { console.log(e); }
  };

  const marquerToutesLues = async () => {
    try {
      for (const n of notifications.filter(n => !n.lu)) {
        await updateDoc(doc(db, 'notifications', n.id), { lu: true });
      }
      setNotifications(ns => ns.map(n => ({ ...n, lu: true })));
    } catch (e) { console.log(e); }
  };

  const ICONS = {
    planning_valide: '✅',
    planning_refuse: '❌',
    action_retard: '⚠️',
    nouveau_client: '👤',
    planning_soumis: '📤',
    objectif_atteint: '🎯',
    message: '💬',
  };

  const COULEURS = {
    planning_valide: '#EAF3DE',
    planning_refuse: '#FCEBEB',
    action_retard: '#FAEEDA',
    nouveau_client: '#E6F1FB',
    planning_soumis: '#FAEEDA',
    objectif_atteint: '#EAF3DE',
    message: '#f5f5f5',
  };

  const nonLues = notifications.filter(n => !n.lu).length;

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Math.round((new Date() - d) / 60000);
    if (diff < 1) return 'À l\'instant';
    if (diff < 60) return `Il y a ${diff} min`;
    if (diff < 1440) return `Il y a ${Math.round(diff / 60)}h`;
    return d.toLocaleDateString('fr-FR');
  };

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>{t(lang, 'notifications')}</div>
          <div style={styles.topbarSub}>{nonLues > 0 ? `${nonLues} non lue(s)` : 'Tout est à jour'}</div>
        </div>
        {nonLues > 0 && (
          <button style={styles.allReadBtn} onClick={marquerToutesLues}>Tout lire</button>
        )}
      </div>

      <div style={styles.content}>
        {loading && <div style={styles.empty}>Chargement...</div>}

        {!loading && notifications.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: 40 }}>🔔</div>
            <div style={{ color: '#888', marginTop: 8 }}>t(lang, 'aucune_notif')</div>
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Vous êtes à jour !</div>
          </div>
        )}

        {notifications.map((n) => (
          <div key={n.id}
            style={{ ...styles.notifCard, background: n.lu ? '#fff' : (COULEURS[n.type] || '#f9f9f9') }}
            onClick={() => !n.lu && marquerLu(n.id)}>
            <div style={styles.notifIcon}>{ICONS[n.type] || '🔔'}</div>
            <div style={{ flex: 1 }}>
              <div style={{ ...styles.notifTitre, fontWeight: n.lue ? 400 : 600 }}>{n.titre}</div>
              <div style={styles.notifMsg}>{n.message}</div>
              <div style={styles.notifDate}>{formatDate(n.createdAt)}</div>
            </div>
            {!n.lu && <div style={styles.notifDot}></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export async function creerNotification(destinataireId, type, titre, message) {
  try {
    await addDoc(collection(db, 'notifications'), {
      destinataireId,
      type,
      titre,
      message,
      lu: false,
      createdAt: serverTimestamp(),
    });
  } catch (e) { console.log('Erreur notification:', e); }
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  allReadBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 11, padding: '5px 10px', cursor: 'pointer' },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { textAlign: 'center', padding: '3rem', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  notifCard: { borderRadius: 10, padding: '12px', display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  notifIcon: { fontSize: 22, flexShrink: 0, marginTop: 2 },
  notifTitre: { fontSize: 13, color: '#1C2833', marginBottom: 3 },
  notifMsg: { fontSize: 12, color: '#666', lineHeight: 1.4 },
  notifDate: { fontSize: 10, color: '#aaa', marginTop: 5 },
  notifDot: { width: 8, height: 8, borderRadius: '50%', background: '#C0392B', flexShrink: 0, marginTop: 6 },
};
